import useApi from "@@hooks/useApi";
import useContent from "@@hooks/useContent";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  GetDamageMatrixResponse,
  GetQualityDetailsQueryResponse,
} from "@inbound/api";
import { useNotification } from "@lux/ds";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createContext, useMemo, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { ReactFCC } from "types/react";
import DamagesModal from "../components/item-review/sections/damage-recognition/components/damages-modal/DamagesModal";
import useItemDetailsContext from "../hooks/useItemDetailsContext";
import {
  editDamageSchema,
  formatDamages,
  getEditDamages,
  IEditDamage,
  IEditDamageSchema,
} from "../schemas/ItemDamageSchema";

interface IDamageRecognitionContext {
  form: UseFormReturn<IEditDamageSchema>;

  getItemQualityDetails: () => void;
  isFetchingItemQualityDetails: boolean;
  itemQualityDetails: GetQualityDetailsQueryResponse | undefined;
  onEditDamagesModelOpen: () => void;

  /** Edit Damages */
  damageCounts: Record<string, number>;
  damageMatrix: GetDamageMatrixResponse[];
  damageThreshold: number;
  scoreCount: number;
}
const DamageRecognitionContext = createContext<IDamageRecognitionContext>({
  form: {} as IDamageRecognitionContext["form"],

  getItemQualityDetails: () => void 0,
  isFetchingItemQualityDetails: false,
  itemQualityDetails: undefined,
  onEditDamagesModelOpen: () => void 0,

  /** Edit Damages */
  damageCounts: {},
  damageMatrix: [],
  damageThreshold: 5,
  scoreCount: 0,
});

const DamageRecognitionProvider: ReactFCC = ({ children }) => {
  const api = useApi();

  const {
    content: { qualityCheck: qualityCheckContent },
  } = useContent();

  const { enqueueNotification } = useNotification();

  const { isCategoryBags, itemDetails } = useItemDetailsContext();

  const [showEditDamagesModal, setShowEditDamagesModal] =
    useState<boolean>(false);

  /** Edit Damages */
  const form = useForm<IEditDamageSchema>({
    mode: "onSubmit",
    resolver: yupResolver(editDamageSchema),
    shouldUnregister: false,
  });

  /** Edit Damages */
  // TODO: Replace with
  const {
    data: { damageMatrix = [], scoreThreshold: damageThreshold = 5 },
  } = useSuspenseQuery({
    queryKey: ["DamageMatrix"],
    queryFn: async () =>
      await api.qualityControl_GetDamageMatrix(null, null, null, null, null),
  });

  const {
    data: itemQualityDetails,
    isLoading: isFetchingItemQualityDetails,
    refetch: getItemQualityDetails,
  } = useQuery({
    queryKey: ["ItemDamages", itemDetails.id],
    queryFn: async () => {
      try {
        const res = await api.products_GetQualityDetails(
          itemDetails.id as string,
        );

        const sortedDamages = (res.damages ?? [])
          .map((damage) => ({
            ...damage,
            parentLocation: damage.parentLocation ?? "Generic",
            score: damage.score ?? 0,
          }))
          .sort((a, b) => b.score - a.score);

        return {
          ...res,
          damages: sortedDamages,
        } as GetQualityDetailsQueryResponse;
      } catch {
        enqueueNotification(
          { title: qualityCheckContent.errorGetDetails },
          { variant: "error", autoHideDuration: 4000 },
        );
      }
    },
    enabled: !!itemDetails.id,
    staleTime: 0,
  });

  const handleOpenModal = () => {
    if (itemQualityDetails?.damages) {
      form.reset(getEditDamages(itemQualityDetails.damages, damageMatrix));
      setShowEditDamagesModal(true);
    }
  };

  const formDamages = form.watch([
    "back",
    "bottom",
    "corners",
    "front",
    "handleOrStrap",
    "leatherOrFabricZipperPull",
    "pocket",
    "side",
    "top",
    "smell",
    "interiorLeatherOrFabricZipperPull",
    "interiorPocket",
    "lining",
    "closure",
    "embellishment",
    "key",
    "practicalAttachment",
    "zipper",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ] as any);

  const scoreCount = useMemo(
    (): number =>
      isCategoryBags
        ? Number(
            (
              Object.values(form.getValues()).reduce(
                (res, damages) =>
                  res +
                  formatDamages(damages)
                    .filter((damage: IEditDamage) => damage.severityId !== null)
                    .reduce(
                      (sum: number, damage: { score: number }) =>
                        sum + (damage.score ?? 0),
                      0,
                    ),
                0,
              ) as number
            ).toFixed(2),
          )
        : 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCategoryBags, form.getValues()],
  );

  const damageCounts = useMemo(
    () =>
      Object.entries(form.getValues()).reduce(
        (res, [key, damages]) => ({
          ...res,
          [key]: formatDamages(damages).filter(
            (damage: IEditDamage) => damage.severityId !== null,
          ).length,
        }),
        {},
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formDamages],
  );

  const handleCloseModal = () => setShowEditDamagesModal(false);

  return (
    <DamageRecognitionContext.Provider
      value={{
        form,
        damageCounts,
        damageMatrix,
        damageThreshold,
        getItemQualityDetails,
        isFetchingItemQualityDetails,
        itemQualityDetails,
        scoreCount,

        onEditDamagesModelOpen: handleOpenModal,
      }}
    >
      {children}

      {showEditDamagesModal ? (
        <DamagesModal onClose={handleCloseModal} />
      ) : null}
    </DamageRecognitionContext.Provider>
  );
};

export { DamageRecognitionContext, DamageRecognitionProvider };
